<template>
    <card>
        <ValidationObserver ref="form" v-slot="{ invalid, pristine }">
            <form
                autocomplete="off"
                @keydown.enter.prevent=""
                @submit.prevent="addUnit"
            >
                <div class="row">
                    <div class="col-md-6">
                        <base-input
                            id="description"
                            v-model="newUnit.description"
                            focus
                            name="description"
                            label="Description"
                        />
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Length (in.)</label>
                            <masked-input
                                v-model="length"
                                class="form-control"
                                name="length"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                            />
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Width (in.)</label>
                            <masked-input
                                v-model="width"
                                class="form-control"
                                name="width"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                            />
                        </div>
                    </div>
                    <div class="col-md-2">
                        <div class="form-group has-label">
                            <label>Height (in.)</label>
                            <masked-input
                                v-model="height"
                                class="form-control"
                                name="height"
                                type="text"
                                :mask="numberMask"
                                :error="errors[0]"
                            />
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <label>Manufacturer</label>
                        <v-select
                            v-model="newUnit.manufacturerId"
                            :reduce="(manufacturer) => manufacturer.id"
                            :options="manufacturers"
                            placeholder="Select manufacturer"
                            label="name"
                            @search="search(...arguments)"
                        >
                            <template slot="no-options">
                                <span v-if="searchText">No results found.</span>
                                <span v-else>Type to search...</span>
                            </template>
                            <template slot="option" slot-scope="option">
                                {{ option.name }}
                            </template>
                        </v-select>
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="relatedNumber"
                            v-model="newUnit.relatedNumber"
                            name="relatedNumber"
                            type="text"
                            label="S/N"
                        />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="trackingNumber"
                            v-model="newUnit.trackingNumber"
                            name="trackingNumber"
                            type="text"
                            label="Tracking No."
                        />
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-3">
                        <base-input
                            id="itemNumber"
                            v-model="newUnit.itemNumber"
                            name="itemNumber"
                            type="text"
                            label="Item No."
                        />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            id="room"
                            v-model="newUnit.room"
                            name="room"
                            type="text"
                            label="Room"
                        />
                    </div>
                    <div class="col-md-3">
                        <div>
                            <label>Quantity</label>
                        </div>
                        <el-input-number v-model="newUnit.quantity" :min="1" />
                    </div>
                    <div class="col-md-3">
                        <base-input
                            name="date"
                            type="date"
                            label="Expected By"
                            :error="errors[0]"
                        >
                            <el-date-picker
                                v-model="newUnit.expectedAt"
                                :picker-options="{
                                    disabledDate: isDateInvalid,
                                }"
                                format="MM/dd/yyyy"
                                type="date"
                                placeholder="Select date"
                            />
                        </base-input>
                    </div>
                </div>
                <div :class="$style.formActions">
                    <base-button
                        id="cancel"
                        class="btn-fill mr-2"
                        @click="cancel"
                    >
                        Cancel
                    </base-button>
                    <base-button
                        id="add"
                        :disabled="invalid || pristine"
                        native-type="submit"
                        type="primary"
                        class="btn-fill"
                    >
                        Add Unit
                    </base-button>
                </div>
            </form>
        </ValidationObserver>
    </card>
</template>
<script>
import MaskedInput from "vue-text-mask";
import { ValidationObserver } from "vee-validate";
import { numberMask } from "src/helpers/inputMasks";
import { DatePicker, InputNumber } from "element-ui";
import vSelect from "vue-select";
import moment from "moment-timezone";

export default {
    name: "PreOrderUnitDetailForm",
    components: {
        ValidationObserver,
        vSelect,
        MaskedInput,
        [DatePicker.name]: DatePicker,
        [InputNumber.name]: InputNumber,
    },
    props: {
        manufacturers: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            searchText: null,
            numberMask,
            newUnit: { quantity: 1 },
        };
    },
    computed: {
        length: {
            get() {
                return this.newUnit.length ? String(this.newUnit.length) : null;
            },
            set(value) {
                this.newUnit.length = parseInt(value, 10);
            },
        },
        width: {
            get() {
                return this.newUnit.width ? String(this.newUnit.width) : null;
            },
            set(value) {
                this.newUnit.width = parseInt(value, 10);
            },
        },
        height: {
            get() {
                return this.newUnit.height ? String(this.newUnit.height) : null;
            },
            set(value) {
                this.newUnit.height = parseInt(value, 10);
            },
        },
    },
    methods: {
        isDateInvalid(date) {
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            return today > date;
        },
        formatDate(date) {
            if (date) {
                return moment(date).format("MM/DD/YYYY");
            }
            return "--";
        },
        formatTime(date) {
            if (date) {
                return moment(date).calendar();
            }
            return "--";
        },
        cancel() {
            this.$emit("cancel");
        },
        addUnit() {
            // remove timezone info
            this.newUnit.expectedAt = this.newUnit.expectedAt.toDateString();
            this.$emit("addUnit", this.newUnit);
        },
        search(search, loading) {
            this.searchText = search;
            this.$emit("searchManufacturers", { search, loading });
        },
    },
};
</script>

<style module>
.formActions {
    display: flex;
    justify-content: space-between;
}
</style>
